import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import { Gateway, GatewayChannel } from '../types';
import { parseFabricUrl } from '../../../utils/channelUtils';

export interface ChannelFlowVisualizationProps {
  channel: {
    name: string;
    config_id: string;
    description?: string;
    listen_address: string;
    target_endpoint: string;
    fabric_target_name?: string;
    protocol?: 'a2a' | 'ap2' | 'mcp' | 'mpx' | 'didcomm';
    channel_type?: 'user' | 'onboarding' | 'system' | 'transit';
    primary_extension?: string;
    supported_extensions?: string[];
    rate_limit?: any;
    enforce_extension?: boolean;
    enforce_response_extension?: boolean;
    custom_metadata?: any;
    response_custom_metadata?: any;
    timeout?: any;
    retry?: any;
    circuit_breaker?: any;
    mirror?: any;
    opa_policy_config?: any;
    mcp_tool_policies_config?: any;
  };
  gateways: Gateway[];
  gatewayChannels: GatewayChannel[];
  channelNameCache: Map<string, string>;
  navigate: (path: string) => void;
}

interface FlowNode {
  id: string;
  label: string;
  type: 'agent' | 'gateway' | 'channel' | 'target' | 'proxy' | 'fabric-gateway';
  x: number;
  y: number;
  details?: string;
  icon?: string;
  deepLink?: string; // Deep link URL for clickable nodes
}

interface FlowLink {
  source: string;
  target: string;
  label?: string;
  type?: 'direct' | 'fabric' | 'proxy';
}

export const ChannelFlowVisualization: React.FC<ChannelFlowVisualizationProps> = ({
  channel,
  gateways,
  gatewayChannels,
  channelNameCache,
  navigate,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Helper function to detect if channel is UCP
  const isUcpChannel = (channelData: any): boolean => {
    const ucpExtension = 'https://ucp.dev/specification/reference?v=2026-01-11';
    return channelData?.primary_extension === ucpExtension || 
           channelData?.supported_extensions?.includes(ucpExtension);
  };

  // Helper function to get display protocol
  const getDisplayProtocol = (channelData: any): string => {
    if (isUcpChannel(channelData)) {
      return 'UCP';
    }
    return channelData?.protocol?.toUpperCase() || 'A2A';
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Optional: Show a toast or brief feedback
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Build flow graph data
  const { nodes, links } = useMemo(() => {
    const nodes: FlowNode[] = [];
    const links: FlowLink[] = [];
    
    let currentX = 100;
    const centerY = 125;
    const horizontalSpacing = 280;

    // 1. Agent (inbound)
    nodes.push({
      id: 'agent',
      label: 'Agent Client',
      type: 'agent',
      x: currentX,
      y: centerY,
      details: 'External agent making requests',
      icon: 'fa-robot',
    });

    currentX += horizontalSpacing;

    // 2. Local Gateway (GW1)
    nodes.push({
      id: 'local-gateway',
      label: 'Local Gateway',
      type: 'gateway',
      x: currentX,
      y: centerY,
      details: 'This gateway instance',
      icon: 'fa-server',
      deepLink: '/gateways/self',
    });

    links.push({
      source: 'agent',
      target: 'local-gateway',
      label: `${channel.listen_address}`,
      type: 'direct',
    });

    currentX += horizontalSpacing;

    // 3. This Gateway / Channel
    nodes.push({
      id: 'gw1-channel',
      label: `Channel: ${channel.name}`,
      type: 'channel',
      x: currentX,
      y: centerY,
      details: `Protocol: ${getDisplayProtocol(channel)}\nType: ${channel.channel_type || 'user'}`,
      icon: 'fa-route',
      deepLink: `/channels/${encodeURIComponent(channel.config_id)}`,
    });

    links.push({
      source: 'local-gateway',
      target: 'gw1-channel',
      label: 'processes via',
      type: 'direct',
    });

    currentX += horizontalSpacing;

    // 4. Analyze target endpoint
    const targetEndpoint = channel.target_endpoint;

    if (targetEndpoint.startsWith('fabric://')) {
      // Fabric protocol - multi-hop
      const parsed = parseFabricUrl(targetEndpoint);
      
      if (parsed) {
        // Remote gateway
        const gateway = gateways.find(g => g.id === parsed.gatewayId);
        const gatewayName = gateway?.name || parsed.gatewayId || 'Unknown Gateway';
        
        nodes.push({
          id: 'fabric-gw',
          label: `Remote Gateway: ${gatewayName}`,
          type: 'fabric-gateway',
          x: currentX,
          y: centerY,
          details: `Gateway ID: ${parsed.gatewayId}\nDIDComm Fabric Connection`,
          icon: 'fa-server',
        });

        links.push({
          source: 'gw1-channel',
          target: 'fabric-gw',
          label: 'DIDComm v2.1',
          type: 'fabric',
        });

        currentX += horizontalSpacing;

        // Remote channel
        let channelName = channel.fabric_target_name;
        if (!channelName && parsed.channelId) {
          channelName = channelNameCache.get(parsed.channelId) || '';
        }
        if (!channelName && parsed.channelId) {
          const channelInList = gatewayChannels.find(c => c.config_id === parsed.channelId);
          channelName = channelInList?.name || '';
        }
        
        const channelLabel = channelName || `Channel ${parsed.channelId?.substring(0, 8)}...`;
        
        nodes.push({
          id: 'remote-channel',
          label: channelLabel,
          type: 'channel',
          x: currentX,
          y: centerY,
          details: `Remote Channel ID: ${parsed.channelId}`,
          icon: 'fa-route',
        });

        links.push({
          source: 'fabric-gw',
          target: 'remote-channel',
          label: 'routes to',
          type: 'direct',
        });

        currentX += horizontalSpacing;

        // Final target (we don't know what the remote channel targets, show placeholder)
        nodes.push({
          id: 'final-target',
          label: 'Downstream Service',
          type: 'target',
          x: currentX,
          y: centerY,
          details: 'Remote channel\'s target endpoint',
          icon: 'fa-hdd',
        });

        links.push({
          source: 'remote-channel',
          target: 'final-target',
          type: 'direct',
        });
      }
    } else if (targetEndpoint.startsWith('proxy://')) {
      // MCP Proxy
      const proxyId = targetEndpoint.substring(8);
      
      nodes.push({
        id: 'mcp-proxy',
        label: `MCP Proxy`,
        type: 'proxy',
        x: currentX,
        y: centerY,
        details: `Proxy ID: ${proxyId}\nJSON-RPC proxy for MCP`,
        icon: 'fa-exchange-alt',
        deepLink: `/mcp-proxies/${encodeURIComponent(proxyId)}`,
      });

      links.push({
        source: 'gw1-channel',
        target: 'mcp-proxy',
        label: 'proxy://',
        type: 'proxy',
      });

      currentX += horizontalSpacing;

      // MCP Server (target of the proxy)
      nodes.push({
        id: 'mcp-server',
        label: 'MCP Server',
        type: 'target',
        x: currentX,
        y: centerY,
        details: 'Model Context Protocol server',
        icon: 'fa-hdd',
      });

      links.push({
        source: 'mcp-proxy',
        target: 'mcp-server',
        label: 'connects to',
        type: 'direct',
      });
    } else if (targetEndpoint.startsWith('did:')) {
      // DID endpoint
      nodes.push({
        id: 'did-target',
        label: 'DID Agent',
        type: 'target',
        x: currentX,
        y: centerY,
        details: targetEndpoint,
        icon: 'fa-fingerprint',
      });

      links.push({
        source: 'gw1-channel',
        target: 'did-target',
        label: 'DID resolution',
        type: 'direct',
      });
    } else {
      // Direct HTTP/HTTPS endpoint
      const url = new URL(targetEndpoint);
      
      // Build a readable label with hostname and port
      let targetLabel = url.hostname;
      if (url.port) {
        targetLabel += `:${url.port}`;
      }
      if (url.pathname && url.pathname !== '/') {
        targetLabel += url.pathname;
      }
      
      nodes.push({
        id: 'http-target',
        label: targetLabel,
        type: 'target',
        x: currentX,
        y: centerY,
        details: targetEndpoint,
        icon: 'fa-hdd',
      });

      links.push({
        source: 'gw1-channel',
        target: 'http-target',
        label: url.protocol.replace(':', ''),
        type: 'direct',
      });
    }

    return { nodes, links };
  }, [channel, gateways, gatewayChannels, channelNameCache]);

  // Render D3 visualization
  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = svgRef.current.clientWidth;

    const g = svg.append('g');

    // Define arrow markers
    svg.append('defs').selectAll('marker')
      .data(['direct', 'fabric', 'proxy'])
      .enter().append('marker')
      .attr('id', d => `arrow-${d}`)
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 48)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', d => {
        if (d === 'fabric') return '#f39c12';
        if (d === 'proxy') return '#9b59b6';
        return '#4e73df';
      });

    // Draw links
    g.selectAll('.link')
      .data(links)
      .enter().append('line')
      .attr('class', 'link')
      .attr('x1', d => {
        const source = nodes.find(n => n.id === d.source);
        return source ? source.x : 0;
      })
      .attr('y1', d => {
        const source = nodes.find(n => n.id === d.source);
        return source ? source.y : 0;
      })
      .attr('x2', d => {
        const target = nodes.find(n => n.id === d.target);
        return target ? target.x : 0;
      })
      .attr('y2', d => {
        const target = nodes.find(n => n.id === d.target);
        return target ? target.y : 0;
      })
      .attr('stroke', d => {
        if (d.type === 'fabric') return '#f39c12';
        if (d.type === 'proxy') return '#9b59b6';
        return '#4e73df';
      })
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', d => d.type === 'fabric' || d.type === 'proxy' ? '5,5' : 'none')
      .attr('marker-end', d => `url(#arrow-${d.type || 'direct'})`);

    // Draw connector lines from labels to edges
    g.selectAll('.label-connector')
      .data(links.filter(l => l.label))
      .enter().append('line')
      .attr('class', 'label-connector')
      .attr('x1', (d, i) => {
        const source = nodes.find(n => n.id === d.source);
        const target = nodes.find(n => n.id === d.target);
        return ((source?.x || 0) + (target?.x || 0)) / 2;
      })
      .attr('y1', (d, i) => {
        const source = nodes.find(n => n.id === d.source);
        const target = nodes.find(n => n.id === d.target);
        const midY = ((source?.y || 0) + (target?.y || 0)) / 2;
        return midY;
      })
      .attr('x2', (d, i) => {
        const source = nodes.find(n => n.id === d.source);
        const target = nodes.find(n => n.id === d.target);
        return ((source?.x || 0) + (target?.x || 0)) / 2;
      })
      .attr('y2', (d, i) => {
        const source = nodes.find(n => n.id === d.source);
        const target = nodes.find(n => n.id === d.target);
        const midY = ((source?.y || 0) + (target?.y || 0)) / 2;
        // Connect to label position (±60)
        return i % 2 === 0 ? midY - 60 : midY + 60;
      })
      .attr('stroke', '#999')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2,2')
      .attr('opacity', 0.5);

    // Draw link labels alternating above/below to avoid collision
    const linkLabels = g.selectAll('.link-label')
      .data(links.filter(l => l.label))
      .enter().append('text')
      .attr('class', 'link-label')
      .attr('x', (d, i) => {
        const source = nodes.find(n => n.id === d.source);
        const target = nodes.find(n => n.id === d.target);
        return ((source?.x || 0) + (target?.x || 0)) / 2;
      })
      .attr('y', (d, i) => {
        const source = nodes.find(n => n.id === d.source);
        const target = nodes.find(n => n.id === d.target);
        const midY = ((source?.y || 0) + (target?.y || 0)) / 2;
        // Alternate above (-60) and below (+60) the edge line to clear circles (radius 45)
        return i % 2 === 0 ? midY - 60 : midY + 60;
      })
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '500')
      .attr('fill', '#666')
      .style('background', 'white')
      .text(d => d.label || '');
    
    // Add tooltips for labels
    linkLabels.append('title')
      .text(d => d.label || '');

    // Draw nodes
    const nodeGroups = g.selectAll('.node')
      .data(nodes)
      .enter().append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x},${d.y})`);

    // Node circles (matching metrics visualization style)
    nodeGroups.append('circle')
      .attr('r', 45)
      .attr('fill', d => {
        switch (d.type) {
          case 'agent': return '#e74a3b';
          case 'gateway': return '#1cc88a';
          case 'channel': return '#4e73df';
          case 'target': return '#36b9cc';
          case 'proxy': return '#9b59b6';
          case 'fabric-gateway': return '#f39c12';
          default: return '#858796';
        }
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 3)
      .attr('opacity', 0.9);

    // Node icons
    nodeGroups.append('text')
      .attr('class', 'node-icon')
      .attr('x', 0)
      .attr('y', 8)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'FontAwesome')
      .attr('font-size', '24px')
      .attr('fill', '#fff')
      .text(d => {
        // FontAwesome unicode characters
        const icons: { [key: string]: string } = {
          'fa-robot': '\uf544',
          'fa-network-wired': '\uf6ff',
          'fa-project-diagram': '\uf542',
          'fa-server': '\uf233',
          'fa-exchange-alt': '\uf362',
          'fa-fingerprint': '\uf577',
          'fa-route': '\uf4d7',
          'fa-hdd': '\uf0a0',
        };
        return icons[d.icon || 'fa-server'] || '\uf233';
      });

    // Node labels (below the circle)
    nodeGroups.append('text')
      .attr('class', 'node-label')
      .attr('x', 0)
      .attr('y', 60)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .attr('fill', d => d.deepLink ? '#4e73df' : '#5a5c69')
      .attr('text-decoration', d => d.deepLink ? 'underline' : 'none')
      .style('cursor', d => d.deepLink ? 'pointer' : 'default')
      .on('click', function(event, d) {
        if (d.deepLink) {
          event.stopPropagation();
          navigate(d.deepLink);
        }
      })
      .each(function(d) {
        const text = d3.select(this);
        const words = d.label.split(/\s+/);
        
        if (words.length > 2) {
          // Multi-line for long labels
          text.text('');
          text.append('tspan')
            .attr('x', 0)
            .attr('dy', 0)
            .text(words.slice(0, 2).join(' '));
          text.append('tspan')
            .attr('x', 0)
            .attr('dy', 12)
            .text(words.slice(2).join(' '));
        } else {
          text.text(d.label);
        }
      });

    // Add tooltips
    nodeGroups.append('title')
      .text(d => `${d.label}\n\n${d.details || ''}`);

    // Calculate bounds and center the visualization
    const minX = Math.min(...nodes.map(n => n.x));
    const maxX = Math.max(...nodes.map(n => n.x));
    const totalWidth = maxX - minX + 200; // Add padding
    const scale = Math.min(1, width / totalWidth);
    const translateX = (width - totalWidth * scale) / 2 - minX * scale + 100;

    g.attr('transform', `translate(${translateX}, 10) scale(${scale})`);

  }, [nodes, links]);

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-light">
        <h6 className="m-0 font-weight-bold text-primary">
          <i className="fas fa-sitemap"></i> Channel Flow Visualization
        </h6>
      </div>
      <div className="card-body">
        <div className="alert alert-info mb-3" style={{ fontSize: '13px' }}>
          <i className="fas fa-info-circle mr-2"></i>
          This diagram shows the complete request flow through this channel, including any gateway hops, 
          MCP proxies, or fabric routing. Gateway DID injection allows tracking multi-hop paths.
        </div>
        
        <div ref={containerRef} style={{ width: '100%' }}>
          <svg
            ref={svgRef}
            style={{ width: '100%', height: '250px', border: '1px solid #e3e6f0', borderRadius: '4px', backgroundColor: '#f8f9fc' }}
          />
        </div>

        {/* Flow details */}
        <div className="mt-3">
          <h6 className="font-weight-bold">Flow Components:</h6>
          <div className="row">
            <div className="col-md-6">
              <ul className="list-unstyled small">
                <li><i className="fas fa-circle text-danger mr-2"></i> <strong>Agent Client</strong> - External agent making requests</li>
                <li><i className="fas fa-circle text-primary mr-2"></i> <strong>Channel</strong> - Gateway channel processing requests</li>
                {nodes.some(n => n.type === 'fabric-gateway') && (
                  <li><i className="fas fa-circle text-warning mr-2"></i> <strong>Fabric Gateway</strong> - Remote gateway connected via DIDComm</li>
                )}
                {nodes.some(n => n.type === 'proxy') && (
                  <li><i className="fas fa-circle mr-2" style={{ color: '#9b59b6' }}></i> <strong>MCP Proxy</strong> - JSON-RPC proxy for MCP protocol</li>
                )}
                <li><i className="fas fa-circle text-info mr-2"></i> <strong>Target</strong> - Final destination service</li>
              </ul>
            </div>
            <div className="col-md-6">
              <ul className="list-unstyled small">
                <li><strong>Protocol:</strong> {getDisplayProtocol(channel)}</li>
                <li>
                  <strong>Listen Address:</strong> <code>{channel.listen_address}</code>
                  <button 
                    className="btn btn-sm btn-link p-0 ml-1" 
                    style={{ fontSize: '12px', verticalAlign: 'middle' }}
                    onClick={() => copyToClipboard(channel.listen_address)}
                    title="Copy to clipboard"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </li>
                <li>
                  <strong>Target Endpoint:</strong> <code style={{ fontSize: '10px' }}>{channel.target_endpoint}</code>
                  <button 
                    className="btn btn-sm btn-link p-0 ml-1" 
                    style={{ fontSize: '12px', verticalAlign: 'middle' }}
                    onClick={() => copyToClipboard(channel.target_endpoint)}
                    title="Copy to clipboard"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </li>
                {channel.rate_limit && (
                  <li><i className="fas fa-tachometer-alt text-warning mr-2"></i> Rate limiting enabled</li>
                )}
                {channel.enforce_extension && (
                  <li><i className="fas fa-shield-alt text-success mr-2"></i> Identity extraction enabled</li>
                )}
                {channel.enforce_response_extension && (
                  <li><i className="fas fa-shield-alt text-success mr-2"></i> Response identity extraction enabled</li>
                )}
                {channel.custom_metadata?.enabled && (
                  <li><i className="fas fa-tags text-info mr-2"></i> Custom metadata injection enabled</li>
                )}
                {channel.response_custom_metadata?.enabled && (
                  <li><i className="fas fa-tags text-info mr-2"></i> Response metadata injection enabled</li>
                )}
                {channel.timeout && (
                  <li><i className="fas fa-clock text-secondary mr-2"></i> Timeout configuration enabled</li>
                )}
                {channel.retry && (
                  <li><i className="fas fa-redo text-primary mr-2"></i> Retry policy enabled</li>
                )}
                {channel.circuit_breaker && (
                  <li><i className="fas fa-power-off text-danger mr-2"></i> Circuit breaker enabled</li>
                )}
                {channel.mirror && (
                  <li><i className="fas fa-clone text-info mr-2"></i> Traffic mirroring enabled</li>
                )}
                {channel.opa_policy_config?.enabled && (
                  <li><i className="fas fa-gavel text-warning mr-2"></i> OPA policy enforcement enabled</li>
                )}
                {channel.mcp_tool_policies_config?.enabled && (
                  <li><i className="fas fa-tools text-warning mr-2"></i> MCP tool policies enabled</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Gateway DID Injection Info */}
        {nodes.some(n => n.type === 'fabric-gateway') && (
          <div className="alert alert-warning mt-3" style={{ fontSize: '12px' }}>
            <i className="fas fa-project-diagram mr-2"></i>
            <strong>Gateway DID Tracking:</strong> This channel uses fabric routing. Each gateway in the path 
            automatically injects its DID into <code>x-affinidi-fabric-gateway-did</code> to provide end-to-end 
            path tracking and audit trails across multiple hops.
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelFlowVisualization;
